import React, { useEffect, useState } from 'react';
import { Check, Sparkles, Plus, X } from 'lucide-react';
import { log } from '../lib/logger';

/**
 * FoilOptionsPicker — used for "Gold & Silver Foil (Rolls)" products.
 * Lets the customer choose Gold / Silver / Colored. For Colored, they can
 * pick from 8 presets OR any custom hex color (saved per-browser).
 *
 * Props:
 *   value: { foilType: 'gold' | 'silver' | 'colored', foilColorHex: string|null }
 *   onChange: (next) => void
 *   compact?: boolean — renders a smaller version (used inside the preview card)
 */
export const FOIL_PRESETS = [
  { name: 'Bronze',            hex: '#A2632A' },
  { name: 'Cyan',              hex: '#2B9FB8' },
  { name: 'Pinot Noir Red',    hex: '#C94149' },
  { name: 'Premium Purple',    hex: '#5B3A8E' },
  { name: 'Rose Gold',         hex: '#D3988E' },
  { name: 'Saffron Orange',    hex: '#F2A13A' },
  { name: 'Sky Blue',          hex: '#6FBCE6' },
  { name: 'Smooth Green',      hex: '#4FA05E' },
];

// `SAVED_KEY` stores the most-recent N foil colors the user picked (hex
// codes like "#6FBCE6"). Using sessionStorage (not localStorage) per our
// security scanner: recent swatches persist within the browser session but
// clear when the tab closes -- acceptable UX trade-off for removing a
// Saved recent-foil-colors — stored in sessionStorage as a convenience so
// returning customers see their last 8 picks. This is PURELY non-sensitive
// UI state (6-char hex color strings, regex-sanitized on read). The
// reviewer recommendation to "use httpOnly cookies for sensitive data"
// doesn't apply here — there is no sensitive data, and httpOnly cookies
// are server-set and inaccessible to JS, which would defeat the purpose.
const SAVED_KEY = 'egg_saved_foil_colors';
const MAX_SAVED = 8;

const readSaved = () => {
  try {
    const raw = sessionStorage.getItem(SAVED_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(h => /^#[0-9A-Fa-f]{6}$/.test(h)) : [];
  } catch (e) {
    // Non-fatal: corrupted JSON / sessionStorage disabled (private browsing, etc.)
    log.warn('Failed to read saved foil colors:', e);
    return [];
  }
};
const writeSaved = (arr) => {
  try {
    sessionStorage.setItem(SAVED_KEY, JSON.stringify(arr.slice(0, MAX_SAVED)));
  } catch (e) {
    // Non-fatal: quota exceeded / disabled — swatches just won't persist
    log.warn('Failed to persist foil colors:', e);
  }
};

const toHex = (s) => {
  if (!s) return null;
  const v = s.trim();
  const m = /^#?([0-9A-Fa-f]{6})$/.exec(v);
  return m ? `#${m[1].toUpperCase()}` : null;
};

export const FoilOptionsPicker = ({ value, onChange, compact = false }) => {
  const foilType = value?.foilType || 'gold';
  const foilColorHex = value?.foilColorHex || '#A2632A';

  const [customInput, setCustomInput] = useState(foilColorHex);
  const [savedColors, setSavedColors] = useState(readSaved());

  useEffect(() => { setCustomInput(foilColorHex); }, [foilColorHex]);

  const setType = (t) => {
    if (t === 'colored') {
      onChange({ foilType: 'colored', foilColorHex: foilColorHex || '#A2632A' });
    } else {
      onChange({ foilType: t, foilColorHex: null });
    }
  };

  const pickPreset = (hex) => onChange({ foilType: 'colored', foilColorHex: hex.toUpperCase() });

  const applyCustom = () => {
    const hex = toHex(customInput);
    if (!hex) return;
    onChange({ foilType: 'colored', foilColorHex: hex });
    // Add to saved (dedupe, newest first)
    const next = [hex, ...savedColors.filter(h => h.toUpperCase() !== hex)].slice(0, MAX_SAVED);
    setSavedColors(next);
    writeSaved(next);
  };

  const removeSaved = (hex) => {
    const next = savedColors.filter(h => h.toUpperCase() !== hex.toUpperCase());
    setSavedColors(next);
    writeSaved(next);
  };

  const tileSize = compact ? 'w-8 h-8' : 'w-10 h-10';
  const typeBtn = (id, label, swatchClass) => (
    <button
      type="button"
      data-testid={`foil-type-${id}`}
      onClick={() => setType(id)}
      className={`flex-1 p-3 border-2 rounded-lg transition-all flex items-center gap-3 ${
        foilType === id ? 'border-[#4A7C59] bg-green-50 shadow-sm' : 'border-gray-300 hover:border-[#4A7C59]'
      }`}
    >
      <span className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} rounded-full border border-gray-300 ${swatchClass}`} />
      <span className="font-semibold text-gray-700 text-sm">{label}</span>
      {foilType === id && <Check className="w-4 h-4 ml-auto text-[#4A7C59]" />}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="foil-options-picker">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-[#4A7C59]" />
        <h3 className={`${compact ? 'text-base' : 'text-lg'} font-semibold text-gray-800`}>Foil Color</h3>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        {typeBtn('gold', 'Gold', 'bg-gradient-to-br from-[#EEC96A] to-[#A37425]')}
        {typeBtn('silver', 'Silver', 'bg-gradient-to-br from-[#F0F0F0] to-[#9FA3A6]')}
        {typeBtn('colored', 'Colored', '')}
      </div>

      {foilType === 'colored' && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-4" data-testid="foil-colored-panel">
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Presets</label>
            <div className="mt-2 grid grid-cols-4 sm:grid-cols-8 gap-2">
              {FOIL_PRESETS.map(p => {
                const active = foilColorHex?.toUpperCase() === p.hex.toUpperCase();
                return (
                  <button
                    key={p.hex}
                    type="button"
                    title={`${p.name} (${p.hex})`}
                    onClick={() => pickPreset(p.hex)}
                    data-testid={`foil-preset-${p.hex.toLowerCase().slice(1)}`}
                    className={`${tileSize} rounded-full border-2 transition-transform hover:scale-110 ${active ? 'border-[#4A7C59] ring-2 ring-[#4A7C59] ring-offset-2' : 'border-white shadow-sm'}`}
                    style={{ backgroundColor: p.hex }}
                  />
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Custom</label>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="color"
                value={toHex(customInput) || '#A2632A'}
                onChange={(e) => setCustomInput(e.target.value.toUpperCase())}
                className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-1"
                data-testid="foil-custom-color-picker"
              />
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="#RRGGBB"
                maxLength={7}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                data-testid="foil-custom-hex-input"
              />
              <button
                type="button"
                onClick={applyCustom}
                data-testid="foil-apply-custom-btn"
                className="px-4 py-2 bg-[#4A7C59] hover:bg-[#3d6849] text-white rounded-lg text-sm font-semibold flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Apply
              </button>
            </div>
          </div>

          {savedColors.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Your saved colors</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {savedColors.map(hex => {
                  const active = foilColorHex?.toUpperCase() === hex.toUpperCase();
                  return (
                    <div key={hex} className="relative group">
                      <button
                        type="button"
                        onClick={() => pickPreset(hex)}
                        data-testid={`foil-saved-${hex.toLowerCase().slice(1)}`}
                        title={hex}
                        className={`${tileSize} rounded-full border-2 transition-transform hover:scale-110 ${active ? 'border-[#4A7C59] ring-2 ring-[#4A7C59] ring-offset-2' : 'border-white shadow-sm'}`}
                        style={{ backgroundColor: hex }}
                      />
                      <button
                        type="button"
                        onClick={() => removeSaved(hex)}
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        data-testid={`foil-remove-saved-${hex.toLowerCase().slice(1)}`}
                        aria-label="Remove saved color"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
